import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, X, Camera, Trash2, Video as VideoIcon, Square, RotateCcw, Circle } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import "./CreateClip.css";

/**
 * Dedicated TikTok-style clip creation page (`/clips/new`).
 *
 * Two modes:
 *  - "upload": pick / drop a video file
 *  - "record": in-browser camera capture via getUserMedia + MediaRecorder (max 60s)
 *
 * Both modes feed the same upload pipeline (POST /api/upload → POST /api/posts).
 */
const MAX_BYTES = 100 * 1024 * 1024; // 100 MB matches /upload limit
const ACCEPT = "video/mp4,video/quicktime,video/webm,video/x-m4v";

export default function CreateClip() {
  const { user } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [mode, setMode] = useState("upload"); // "upload" | "record"
  const [localUrl, setLocalUrl] = useState("");
  const [remoteUrl, setRemoteUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);

  // Sign-in gate
  useEffect(() => {
    if (!user) {
      toast.error("Sign in to post a clip");
      nav("/auth?mode=login");
    }
  }, [user, nav]);

  // Clean up the object URL when leaving the page
  useEffect(() => () => { if (localUrl) URL.revokeObjectURL(localUrl); }, [localUrl]);

  const uploadBlob = async (blob, filename) => {
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      const path = data.url.startsWith("/api") ? data.url.slice(4) : data.url;
      setRemoteUrl(`${API}${path}`);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
      setLocalUrl("");
    } finally { setUploading(false); }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please choose a video file");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Max 100 MB — your file is ${(file.size / 1024 / 1024).toFixed(1)} MB`);
      return;
    }
    const url = URL.createObjectURL(file);
    setLocalUrl(url);
    setRemoteUrl("");
    await uploadBlob(file, file.name || "clip.mp4");
  };

  const onRecordingComplete = async (blob) => {
    if (blob.size > MAX_BYTES) {
      toast.error("Recording exceeded 100 MB — please try a shorter take");
      return;
    }
    const url = URL.createObjectURL(blob);
    if (localUrl) URL.revokeObjectURL(localUrl);
    setLocalUrl(url);
    setRemoteUrl("");
    const ext = (blob.type.includes("mp4") ? "mp4" : "webm");
    await uploadBlob(blob, `clip-${Date.now()}.${ext}`);
  };

  const clearFile = () => {
    if (localUrl) URL.revokeObjectURL(localUrl);
    setLocalUrl("");
    setRemoteUrl("");
    setProgress(0);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!remoteUrl) {
      toast.error("Pick or record a video first");
      return;
    }
    setPosting(true);
    try {
      await api.post("/posts", {
        type: "VIDEO",
        content: caption.trim(),
        mediaUrl: remoteUrl,
      });
      toast.success("Clip posted");
      nav("/clips");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post clip");
    } finally { setPosting(false); }
  };

  return (
    <div className="create-clip-page" data-testid="create-clip-page">
      <div className="create-clip-topbar">
        <button onClick={() => nav(-1)} className="create-clip-topbar-btn" aria-label="Close" data-testid="create-clip-close-btn">
          <X size={20} strokeWidth={1.5} />
        </button>
        <span className="create-clip-title">Post a Clip</span>
        <div style={{ width: 40 }} />
      </div>

      <div className="create-clip-mode-tabs" data-testid="create-clip-mode-tabs">
        <button
          onClick={() => { if (localUrl) clearFile(); setMode("upload"); }}
          className={`create-clip-mode-tab ${mode === "upload" ? "active" : ""}`}
          data-testid="create-clip-mode-upload"
        >
          <Upload size={14} strokeWidth={1.5} /> Upload
        </button>
        <button
          onClick={() => { if (localUrl) clearFile(); setMode("record"); }}
          className={`create-clip-mode-tab ${mode === "record" ? "active" : ""}`}
          data-testid="create-clip-mode-record"
        >
          <VideoIcon size={14} strokeWidth={1.5} /> Record
        </button>
      </div>

      <div className="create-clip-body">
        {mode === "upload" ? (
          <label className="clip-upload-zone" data-testid="create-clip-upload-zone">
            {localUrl ? (
              <>
                <video src={localUrl} controls playsInline muted />
                <div className="overlay">
                  <button type="button" onClick={(e) => { e.preventDefault(); clearFile(); }} aria-label="Remove" data-testid="create-clip-remove-btn">
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="clip-upload-icon">
                  <Camera size={28} strokeWidth={1.5} />
                </div>
                <div className="clip-upload-cta">Tap to choose a clip</div>
                <div className="clip-upload-hint">9:16 · up to 100 MB · MP4, MOV, WebM</div>
                <div className="clip-upload-hint" style={{ marginTop: -8 }}>
                  <Upload size={12} strokeWidth={1.5} style={{ verticalAlign: "middle" }} /> &nbsp;or drag &amp; drop
                </div>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              onChange={(e) => handleFile(e.target.files?.[0])}
              style={{ display: "none" }}
              data-testid="create-clip-file-input"
            />
          </label>
        ) : (
          <RecordPanel
            previewUrl={localUrl}
            onComplete={onRecordingComplete}
            onClear={clearFile}
          />
        )}

        {uploading && (
          <div className="clip-progress-track" data-testid="create-clip-progress">
            <div className="clip-progress-fill-up" style={{ width: `${progress}%` }} />
          </div>
        )}

        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value.slice(0, 280))}
          placeholder="Add a caption… (optional)"
          rows={3}
          className="clip-caption-input"
          maxLength={280}
          data-testid="create-clip-caption-input"
        />
        <div style={{ alignSelf: "flex-end", maxWidth: 480, width: "100%", textAlign: "right", marginTop: -16, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
          {caption.length}/280
        </div>

        <button
          onClick={submit}
          disabled={!remoteUrl || uploading || posting}
          className="clip-post-btn"
          data-testid="create-clip-post-btn"
        >
          {posting ? "Posting…" : uploading ? "Uploading…" : "Post Clip"}
        </button>
      </div>
    </div>
  );
}

/**
 * In-browser camera capture. Uses getUserMedia for live preview + MediaRecorder
 * for recording. User picks 15s / 30s / 60s; auto-stops at the cap.
 */
function RecordPanel({ previewUrl, onComplete, onClear }) {
  const livePreviewRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const [permError, setPermError] = useState("");
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [maxSec, setMaxSec] = useState(60); // 15 | 30 | 60
  const [countdown, setCountdown] = useState(0); // 3 → 2 → 1 → 0 starts

  // Acquire camera + mic when this panel mounts (only when no previewUrl yet).
  useEffect(() => {
    if (previewUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: { facingMode: "user", width: { ideal: 1080 }, height: { ideal: 1920 } },
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (livePreviewRef.current) {
          livePreviewRef.current.srcObject = stream;
        }
      } catch (e) {
        setPermError(e?.message || "Camera permission denied");
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    };
  }, [previewUrl]);

  const pickMimeType = () => {
    const types = [
      "video/mp4;codecs=h264,aac",
      "video/mp4",
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
    ];
    for (const t of types) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(t)) return t;
    }
    return "";
  };

  const startRecording = () => {
    if (!streamRef.current) {
      toast.error("Camera isn't ready yet");
      return;
    }
    chunksRef.current = [];
    const mimeType = pickMimeType();
    let rec;
    try {
      rec = mimeType
        ? new MediaRecorder(streamRef.current, { mimeType })
        : new MediaRecorder(streamRef.current);
    } catch (e) {
      toast.error("Recording not supported in this browser");
      return;
    }
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || mimeType || "video/webm" });
      chunksRef.current = [];
      onComplete(blob);
    };
    rec.start(250); // collect 250ms chunks
    recorderRef.current = rec;
    setRecording(true);
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed((s) => {
        const next = s + 1;
        if (next >= maxSec) {
          stopRecording();
          return maxSec;
        }
        return next;
      });
    }, 1000);
  };

  const beginCountdown = () => {
    if (!streamRef.current) {
      toast.error("Camera isn't ready yet");
      return;
    }
    if (countdown > 0 || recording) return;
    setCountdown(3);
    countdownRef.current = setInterval(() => {
      setCountdown((n) => {
        if (n <= 1) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          startRecording();
          return 0;
        }
        return n - 1;
      });
    }, 1000);
  };

  const cancelCountdown = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setCountdown(0);
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    setCountdown(0);
    setRecording(false);
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch { /* noop */ }
    }
  };

  const retake = () => {
    onClear();
    setElapsed(0);
    setRecording(false);
  };

  if (permError) {
    return (
      <div className="clip-upload-zone" data-testid="record-perm-error" style={{ cursor: "default" }}>
        <div className="clip-upload-icon" style={{ background: "rgba(217, 92, 20, 0.18)" }}>
          <VideoIcon size={28} strokeWidth={1.5} />
        </div>
        <div className="clip-upload-cta">Camera permission needed</div>
        <div className="clip-upload-hint" style={{ textAlign: "center", padding: "0 16px" }}>
          Allow camera + microphone access in your browser settings to record.
        </div>
      </div>
    );
  }

  const pct = Math.min(100, (elapsed / maxSec) * 100);

  return (
    <div className="clip-upload-zone" data-testid="record-panel" style={{ cursor: "default", borderStyle: "solid", borderColor: recording ? "#ef4444" : "rgba(217, 92, 20, 0.55)" }}>
      {previewUrl ? (
        <>
          <video src={previewUrl} controls playsInline muted data-testid="record-preview-video" />
          <div className="overlay" style={{ alignItems: "flex-start", justifyContent: "flex-end" }}>
            <button type="button" onClick={retake} aria-label="Retake" data-testid="record-retake-btn" title="Retake">
              <RotateCcw size={16} strokeWidth={1.5} />
            </button>
          </div>
        </>
      ) : (
        <>
          <video
            ref={livePreviewRef}
            autoPlay
            playsInline
            muted
            data-testid="record-live-preview"
            style={{ transform: "scaleX(-1)" /* mirror selfie */ }}
          />

          {recording && (
            <div className="record-timer" data-testid="record-timer">
              <span className="record-dot" /> {String(Math.floor(elapsed / 60)).padStart(2, "0")}:{String(elapsed % 60).padStart(2, "0")}
            </div>
          )}

          {countdown > 0 && (
            <div className="record-countdown-overlay" onClick={cancelCountdown} data-testid="record-countdown">
              <div className="record-countdown-number" key={countdown} data-testid={`record-countdown-${countdown}`}>{countdown}</div>
              <div className="record-countdown-hint">tap to cancel</div>
            </div>
          )}

          {/* Bottom-center record control */}
          <div className="record-controls">
            <button
              type="button"
              onClick={recording ? stopRecording : (countdown > 0 ? cancelCountdown : beginCountdown)}
              className={`record-btn ${recording ? "recording" : ""} ${countdown > 0 ? "counting" : ""}`}
              data-testid={recording ? "record-stop-btn" : "record-start-btn"}
              aria-label={recording ? "Stop recording" : (countdown > 0 ? "Cancel countdown" : "Start recording")}
              disabled={false}
            >
              {recording ? <Square size={22} strokeWidth={2} fill="currentColor" /> : <Circle size={22} strokeWidth={2} fill="currentColor" />}
            </button>
          </div>

          {/* Bottom progress bar */}
          <div className="record-progress" style={{ width: `${pct}%` }} data-testid="record-progress" />

          {/* Duration selector chips */}
          {!recording && countdown === 0 && (
            <div className="record-duration-chips" data-testid="record-duration-chips">
              {[15, 30, 60].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setMaxSec(s)}
                  className={`record-duration-chip ${maxSec === s ? "active" : ""}`}
                  data-testid={`record-duration-${s}s`}
                  aria-pressed={maxSec === s}
                >
                  {s}s
                </button>
              ))}
            </div>
          )}

          {!recording && countdown === 0 && (
            <div className="clip-upload-hint" style={{ position: "absolute", top: 16, left: 0, right: 0, textAlign: "center", zIndex: 2 }}>
              Up to {maxSec}s · 9:16
            </div>
          )}
        </>
      )}
    </div>
  );
}
